// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a cited passage (Agents Cite) as one quiet row: the quote's bar and its lines side by side,
// cut to the width. A quote longer than the row opens to the whole passage: press it (or its chevron in
// a sent prompt); on a desktop, resting the pointer on it shows the whole passage too. The prompt box
// (ComposerQuoteNode) and sent prompts use the same row; the prompt box adds a × that takes it out.

import styles from '@app/features/cited_quote/CitedQuote.module.css';
import {quoteRow} from '@app/features/cited_quote/QuoteText';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconChevronDown, IconX} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {useLayoutEffect, useRef, useState} from 'react';

const EXPAND_DESCRIPTOR = msg({
	message: 'Show the whole quote',
	comment: 'Agents: a quoted passage shown as one row in a prompt; this opens it to show all of it.',
});
const REMOVE_DESCRIPTOR = msg({
	message: 'Remove quote',
	comment: 'Agents prompt box: take a cited passage out of the prompt.',
});

/** Whether the one-row text is cut off, so the row can open. */
function useCut(text: string, open: boolean): [React.RefObject<HTMLSpanElement | null>, boolean] {
	const ref = useRef<HTMLSpanElement | null>(null);
	const [cut, setCut] = useState(false);
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element || open) return;
		const measure = () => setCut(element.scrollWidth > element.clientWidth + 1);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		return () => observer.disconnect();
	}, [text, open]);
	return [ref, cut];
}

export function CitedQuote({
	text,
	variant,
	onRemove,
	className,
	flx,
}: {
	/** The passage, one line per paragraph or list point. */
	text: string;
	/** In the prompt box the whole row opens it; in a sent prompt a press jumps to the passage, so the chevron opens it. */
	variant: 'composer' | 'sent';
	onRemove?: () => void;
	className?: string;
	flx: string;
}) {
	const {i18n} = useLingui();
	const [open, setOpen] = useState(false);
	const row = quoteRow(text);
	const [rowRef, cut] = useCut(row, open);
	const lines = text.split('\n').filter((line) => line.trim()).length;
	const opens = open || cut || lines > 1;
	const expandLabel = i18n._(EXPAND_DESCRIPTOR);
	const toggle = (event: React.MouseEvent) => {
		event.stopPropagation();
		if (opens) setOpen((value) => !value);
	};
	const body = open ? (
		<span className={styles.full}>{text}</span>
	) : (
		<span ref={rowRef} className={styles.row}>
			{row}
		</span>
	);
	const chevron = opens && <IconChevronDown size={14} stroke={2} className={styles.chevron} aria-hidden="true" />;
	const peek = (element: React.ReactElement) =>
		opens && !open ? (
			<Tooltip text={() => <span className={styles.peek}>{text}</span>} position="top" maxWidth="xl" delay={350}>
				{element}
			</Tooltip>
		) : (
			element
		);
	return (
		<blockquote
			className={clsx(styles.quote, open && styles.open, onRemove && styles.removable, className)}
			data-quote={text}
			data-open={open || undefined}
			contentEditable={false}
			data-flx={flx}
		>
			{variant === 'composer' ? (
				peek(
					<button
						type="button"
						className={styles.toggle}
						onMouseDown={(event) => event.preventDefault()}
						onClick={toggle}
						aria-expanded={opens ? open : undefined}
						aria-label={opens ? expandLabel : undefined}
						data-flx={`${flx}.toggle`}
					>
						{body}
						{chevron}
					</button>,
				)
			) : (
				<>
					{peek(<span className={styles.text}>{body}</span>)}
					{opens && (
						<button
							type="button"
							className={styles.chevronButton}
							onClick={toggle}
							aria-expanded={open}
							aria-label={expandLabel}
							data-flx={`${flx}.toggle`}
						>
							{chevron}
						</button>
					)}
				</>
			)}
			{onRemove && (
				<button
					type="button"
					className={styles.remove}
					// Keeps the caret where it was.
					onMouseDown={(event) => event.preventDefault()}
					onClick={(event) => {
						event.stopPropagation();
						onRemove();
					}}
					aria-label={i18n._(REMOVE_DESCRIPTOR)}
					title={i18n._(REMOVE_DESCRIPTOR)}
					data-flx={`${flx}.remove`}
				>
					<IconX size={13} stroke={2.2} />
				</button>
			)}
		</blockquote>
	);
}
